import { useRef, useState } from 'react';
import { useSaveCoverageEligibilityMutation } from '@features/patient-coverages/store/api';
import type {
  EligibilityCurrentResponse,
  EligibilityManualRequest,
} from '@features/patient-coverages/types';
import { Alert, Button, Stack } from '@mui/material';

import type { ManualEligibilityDraft } from '../hooks/useManualEligibilityDraft';
import { eligibilityWriteError, manualEligibilityCommand } from '../utils/manualEligibilityCommand';

import { CoverageRecordSummary } from './CoverageRecordSummary';
import { ManualEligibilityDialog } from './ManualEligibilityDialog';

export function CoverageManualSession({
  current,
  label,
  allowed,
  recoveryMessage,
  onRetryDetails,
  onClose,
  onSaved,
  saveManual,
  isSaving = false,
}: {
  current: EligibilityCurrentResponse;
  label: string;
  allowed: boolean;
  recoveryMessage?: string | null;
  onRetryDetails?: () => void;
  onClose: () => void;
  onSaved: () => void;
  saveManual?: (request: EligibilityManualRequest) => Promise<unknown>;
  isSaving?: boolean;
}) {
  const [basis, setBasis] = useState(current);
  const [stale, setStale] = useState<EligibilityCurrentResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const request = useRef<EligibilityManualRequest | null>(null);
  const [save, { isLoading }] = useSaveCoverageEligibilityMutation();
  async function submit(draft: ManualEligibilityDraft) {
    if (stale) return;
    const candidate = manualEligibilityCommand(
      draft,
      basis,
      request.current?.idempotency_key ?? crypto.randomUUID(),
    );
    if (request.current && JSON.stringify(candidate) !== JSON.stringify(request.current))
      candidate.idempotency_key = crypto.randomUUID();
    request.current = candidate;
    setError(null);
    try {
      if (saveManual) {
        await saveManual(candidate);
      } else {
        await save({
          patientId: basis.patient_id,
          coverageId: basis.patient_insurance_coverage_id,
          eligibilityManualRequest: candidate,
        }).unwrap();
      }
      onSaved();
    } catch (failure) {
      const detail = eligibilityWriteError(failure);
      if (
        detail &&
        'current' in detail &&
        detail.current.patient_id === basis.patient_id &&
        detail.current.patient_insurance_coverage_id === basis.patient_insurance_coverage_id
      ) {
        setStale(detail.current);
        setError(
          'The record changed. Your entries are preserved. Review the latest values before saving again.',
        );
      } else
        setError(
          detail?.error === 'eligibility_command_in_progress'
            ? 'This save is still processing. Your entries are preserved; retry to check the same command.'
            : detail?.error === 'eligibility_idempotency_conflict'
              ? 'This command was used for different input. Close and reopen the entry after reviewing current values.'
              : 'Could not save benefits. Your entries are preserved. Retry the same save.',
        );
    }
  }
  return (
    <ManualEligibilityDialog
      initialRecord={current}
      coverageLabel={label}
      allowed={allowed}
      isSaving={isLoading || isSaving}
      saveBlocked={!!stale}
      error={recoveryMessage ?? error}
      onClose={onClose}
      onSave={submit}
      comparison={
        <>
          {recoveryMessage && onRetryDetails && (
            <Button onClick={onRetryDetails}>Retry coverage details</Button>
          )}
          {stale && (
            <Stack spacing={1}>
              <Alert severity="warning">Latest values for this coverage</Alert>
              <CoverageRecordSummary current={stale} />
              <Button
                onClick={() => {
                  setBasis(stale);
                  setStale(null);
                  request.current = null;
                  setError(
                    'Latest values reviewed. Your draft is unchanged; save to confirm your entries.',
                  );
                }}
              >
                Use this comparison and keep my draft
              </Button>
            </Stack>
          )}
        </>
      }
    />
  );
}
